import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Phase 2.4 Tier 2 — Receipts tab controller. Opens a modal
// pre-populated with the remaining-to-receive qty per line,
// lets the receiver edit per-line quantities, posts a single receipt
// record. `Receive Remaining` pre-fills every line's remaining qty at
// once for the common "truck finally showed up with the last half of
// the order" case.
export default class extends Controller {
  static targets = [
    "recordModal", "receivedOn", "slipNumber", "notes", "linesBody",
    "receiptBody", "chevron"
  ]

  static values = {
    projectId: Number,
    poId: Number,
    lines: Array
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
  }

  // Open the modal with each line pre-filled to 0 (user fills in the
  // qty that actually showed up).
  openRecordModal() {
    this._populateLines(false)
    this._show(this.recordModalTarget)
  }

  // Shortcut: open the modal with every line's "remaining" qty
  // pre-filled (user just confirms or trims if partial).
  openReceiveRemainingModal() {
    this._populateLines(true)
    this._show(this.recordModalTarget)
  }

  hideRecordModal() {
    this._hide(this.recordModalTarget)
  }

  async confirmRecord() {
    const rows = this.linesBodyTarget.querySelectorAll("[data-line-id]")
    const lines = []
    rows.forEach(row => {
      const lineId = parseInt(row.dataset.lineId, 10)
      const qty = parseFloat(row.querySelector('[data-field="qty"]').value) || 0
      if (qty > 0) lines.push({ purchase_order_line_item_id: lineId, received_qty: qty })
    })

    if (lines.length === 0) {
      this._showToast("Enter a quantity on at least one line", "error")
      return
    }

    const body = {
      purchase_order_receipt: {
        received_on: this.receivedOnTarget.value,
        packing_slip_number: this.slipNumberTarget.value.trim() || null,
        notes: this.notesTarget.value.trim() || null,
        line_items: lines
      }
    }
    const resp = await this._ajax("POST", this._receiptsUrl(), body)
    if (!resp.ok) {
      this._showToast(resp.body?.errors?.join(", ") || resp.body?.error || "Could not save receipt", "error")
      return
    }
    this._track("Purchase Order Receipt Recorded", { receipt_id: resp.body.id })
    this._flashAndReload("Receipt recorded")
  }

  async deleteReceipt(event) {
    const receiptId = event.currentTarget.dataset.receiptId
    const resp = await this._ajax("DELETE", `${this._receiptsUrl()}/${receiptId}`)
    if (!resp.ok) { this._showToast(resp.body?.error || "Delete failed", "error"); return }
    this._flashAndReload("Receipt removed")
  }

  toggleReceipt(event) {
    const card = event.currentTarget.closest(".bg-white")
    const body = card?.querySelector('[data-po-receipts-target="receiptBody"]')
    const chevron = card?.querySelector('[data-po-receipts-target="chevron"]')
    if (!body) return
    body.classList.toggle("hidden")
    if (chevron) chevron.classList.toggle("rotate-180")
  }

  stopPropagation(event) { event.stopPropagation() }

  // ── internals ─────────────────────────────────────────────────────
  _populateLines(prefillRemaining) {
    const body = this.linesBodyTarget
    body.innerHTML = ""

    this.linesValue.forEach(line => {
      const tr = document.createElement("tr")
      tr.dataset.lineId = line.id
      const defaultQty = prefillRemaining ? line.remaining : 0
      tr.innerHTML = `
        <td class="px-2 py-1 text-gray-500">${line.position}</td>
        <td class="px-2 py-1">${this._escape(line.description || "")}</td>
        <td class="px-2 py-1 text-right tabular-nums">${this._fmt(line.qty_ordered)}</td>
        <td class="px-2 py-1 text-right tabular-nums text-gray-500">${this._fmt(line.received_to_date)}</td>
        <td class="px-2 py-1 text-right">
          <input type="number" step="0.01" min="0" value="${this._fmt(defaultQty)}"
                 data-field="qty"
                 class="w-24 text-[13px] text-right border border-gray-300 rounded px-1 py-0.5 tabular-nums">
        </td>
      `
      body.appendChild(tr)
    })
  }

  _fmt(n) {
    const v = Number(n) || 0
    return v === Math.trunc(v) ? String(Math.trunc(v)) : v.toFixed(2)
  }

  _escape(str) {
    return String(str ?? "").replace(/[&<>"']/g, ch => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[ch]))
  }

  _receiptsUrl() {
    return `/projects/${this.projectIdValue}/purchase_orders/${this.poIdValue}/receipts`
  }

  _show(el) { el?.classList.remove("hidden") }
  _hide(el) { el?.classList.add("hidden") }

  _flashAndReload(message, type = "success") {
    sessionStorage.setItem("purchase_order_flash", JSON.stringify({ message, type }))
    window.location.reload()
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(msg)
    document.body.prepend(el)
  }

  async _ajax(method, url, payload) {
    const options = {
      method,
      headers: { "X-CSRF-Token": this._csrfToken, "Accept": "application/json" }
    }
    if (payload !== undefined && method !== "GET") {
      options.headers["Content-Type"] = "application/json"
      options.body = JSON.stringify(payload)
    }
    try {
      const resp = await fetch(url, options)
      const ct = resp.headers.get("Content-Type") || ""
      const body = ct.includes("application/json") ? await resp.json().catch(() => null) : null
      return { ok: resp.ok, status: resp.status, body }
    } catch (e) {
      return { ok: false, status: 0, body: { error: e.message } }
    }
  }

  _track(name, props = {}) {
    try {
      mixpanelTrack(name, { project_id: this.projectIdValue, purchase_order_id: this.poIdValue, ...props })
    } catch { /* ignore */ }
  }
}
